import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { listTrackingEventTypes } from '../API/trackingEventType';
import { fetchAllPages } from '../utils/pagination';
import { TRACKING_EVENT_TYPES } from '../API/trackingEvent';
import { getTrackingEventTypeCatalogDisplayName } from '../utils/trackingEventLabels';

function buildStaticCatalogRows() {
  return TRACKING_EVENT_TYPES.map((o) => ({
    code: o.value,
    name: o.label,
    name_ar: '',
    sort_order: 0,
  }));
}

function mapRowsToOptions(rows, language) {
  return rows.map((r) => ({
    value: r.code,
    label: getTrackingEventTypeCatalogDisplayName(r, language) || r.code,
  }));
}

/**
 * Loads event types from GET /api/tracking_event_type/ when permitted; otherwise keeps static TRACKING_EVENT_TYPES.
 * Options shape: { value: code, label: localized display name } sorted by sort_order.
 * Labels follow the active UI language (ar/zh/en) on first paint and when language changes.
 * @param {boolean} [enabled=true] — When false, skips the request (e.g. missing list permission).
 */
export default function useTrackingEventTypeOptions(enabled = true) {
  const { i18n } = useTranslation('ui');
  const [catalogRows, setCatalogRows] = useState(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setCatalogRows(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      setLoading(true);
      try {
        const rows = await fetchAllPages(
          (page, pageSize) => listTrackingEventTypes({ page, page_size: pageSize }),
        );
        if (!cancelled && rows.length > 0) {
          const sorted = [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          setCatalogRows(sorted);
        } else if (!cancelled) {
          setCatalogRows(null);
        }
      } catch (_) {
        /* 403 or network: keep static fallback */
        if (!cancelled) setCatalogRows(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const options = useMemo(() => {
    const language = i18n.language;
    if (catalogRows && catalogRows.length > 0) {
      return mapRowsToOptions(catalogRows, language);
    }
    return mapRowsToOptions(buildStaticCatalogRows(), language);
  }, [catalogRows, i18n.language]);

  return { options, loading };
}
